import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp, SectionHead } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { ASSETS } from "@/data/site";
import { BRANDS, PRODUCTS } from "@/data/products";

function BrandPanel({ brand, logo, reverse, testId }) {
  const items = PRODUCTS.filter((p) => p.brand === brand.name).slice(0, 4);
  const isMaroon = brand.accent === "maroon";
  return (
    <section className={`py-24 sm:py-32 ${isMaroon ? "bg-ink" : "bg-coal border-y border-white/10"}`} data-testid={testId}>
      <div className={`max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <FadeUp>
          <div className="relative">
            <div className={`absolute -inset-4 border ${isMaroon ? "border-maroon-light/40" : "border-emerald-light/40"} rounded-sm`} />
            <div className="relative bg-white rounded-sm p-10 sm:p-14 flex items-center justify-center">
              <img src={logo} alt={`${brand.name} official logo`} className="w-full max-w-sm h-auto" data-testid={`${testId}-logo`} />
            </div>
          </div>
        </FadeUp>
        <div>
          <FadeUp>
            <p className={`text-[11px] uppercase tracking-overline mb-5 ${isMaroon ? "text-maroon-light" : "text-emerald-light"}`}>{brand.market}</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tight text-cream">{brand.name}</h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <ul className="mt-8 space-y-4">
              {brand.points.map((pt) => (
                <li key={pt} className="flex gap-4 text-sm md:text-base text-zinc-300 font-light">
                  <span className="w-2 h-2 rotate-45 bg-gold shrink-0 mt-1.5" /> {pt}
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-10 flex flex-wrap gap-4">
            <Link to="/products" data-testid={`${testId}-products-button`} className="inline-flex items-center gap-2 bg-gold text-ink text-xs font-semibold uppercase tracking-[0.14em] px-6 py-3.5 rounded-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(212,175,55,0.35)]">
              View {brand.name} Range <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/export-inquiry" data-testid={`${testId}-enquiry-button`} className="inline-flex items-center gap-2 border border-gold/60 text-gold text-xs font-semibold uppercase tracking-[0.14em] px-6 py-3.5 rounded-sm transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink">
              Trade Enquiry <ArrowUpRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.08}>
              <div className="group bg-ink border border-white/10 rounded-sm overflow-hidden transition-[border-color,transform] duration-500 hover:border-gold/40 hover:-translate-y-1">
                <div className="aspect-square overflow-hidden">
                  <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-4">
                  <p className="text-[9px] uppercase tracking-[0.22em] text-gold">{p.category}</p>
                  <p className="mt-1 font-display text-base text-cream leading-snug">{p.name}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Brands() {
  return (
    <>
      <section className="pt-40 pb-20 bg-ink" data-testid="brands-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">Our Brands</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              One house.<br /><em className="text-gold italic">Two flags.</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-400 font-light leading-relaxed">
              Waghe Shree carries sixty years of Indian trust. Tasty Mint carries it across borders.
            </p>
          </FadeUp>
        </div>
      </section>

      <BrandPanel brand={BRANDS.shree} logo={ASSETS.logoShree} testId="brand-panel-shree" />
      <BrandPanel brand={BRANDS.mint} logo={ASSETS.logoMint} reverse testId="brand-panel-mint" />
      <EditorialMarquee />
    </>
  );
}
